import React, { useState, useEffect } from 'react';
import { Drawer, Typography, IconButton, Box, Select, MenuItem } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import Button from '@mui/material/Button';

// Custom chevron for select
const CustomChevron = (props) => (
    <svg {...props} style={{ right: 16, position: 'absolute', pointerEvents: 'none', color: '#666' }} width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

// Custom dark styled input component
const DarkInput = ({ label, value, onChange, placeholder, select, children, endAdornment, type = "text", error }) => (
    <Box sx={{ mb: 2.5 }}>
        <Typography variant="caption" sx={{ color: error ? '#e57373' : '#aaa', mb: 0.5, display: 'block', fontWeight: error ? 'bold' : 'normal' }}>{label}</Typography>
        <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            backgroundColor: '#111', 
            border: error ? '1px solid #e57373' : '1px solid #333', 
            borderRadius: '6px',
            overflow: 'hidden',
            transition: 'border-color 0.2s ease',
            '&:focus-within': { border: error ? '1px solid #e57373' : '1px solid #3F51B5' }
        }}>
            {select ? (
                <Select
                    value={value}
                    onChange={onChange}
                    displayEmpty
                    IconComponent={CustomChevron}
                    sx={{
                        width: '100%',
                        color: '#fff',
                        '.MuiOutlinedInput-notchedOutline': { border: 0 },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 0 },
                        '.MuiSelect-select': { padding: '12px', backgroundColor: 'transparent' }
                    }}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                bgcolor: '#1e1e1e',
                                color: '#fff',
                                mt: 1,
                                borderRadius: '8px',
                                border: '1px solid #333',
                                '& .MuiMenuItem-root': {
                                    px: 2,
                                    py: 1.5,
                                    '&:hover': { bgcolor: '#333' },
                                    '&.Mui-selected': { bgcolor: '#3F51B5 !important' },
                                    '&.Mui-selected:hover': { bgcolor: '#4F5BCD !important' }
                                }
                            }
                        }
                    }}
                >
                    <MenuItem value="" disabled sx={{ color: '#888', display: value ? 'none' : 'block' }}>Select Task Type</MenuItem>
                    {children}
                </Select>
            ) : (
                <input 
                    type={type}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    style={{ 
                        width: '100%', padding: '12px', backgroundColor: 'transparent', color: '#fff', 
                        border: 'none', outline: 'none', colorScheme: 'dark'
                    }}
                />
            )}
            {endAdornment && (
                <Box sx={{ pr: 2, pl: 1, color: '#aaa', fontSize: '0.85rem', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                    {endAdornment}
                </Box>
            )}
        </Box>
        {error && (
            <Typography variant="caption" sx={{ color: '#e57373', mt: 0.5, display: 'block', fontSize: '0.75rem' }}>
                {error}
            </Typography>
        )}
    </Box>
);

export default function TaskDialog({
    open,
    onClose,
    isEditMode,
    taskDetails,
    setTaskDetails,
    handleSaveTask,
}) {
    const [errors, setErrors] = useState({});

    // Reset errors when dialog open state changes
    useEffect(() => {
        if (open) {
            setErrors({});
        }
    }, [open]);

    const handleValidateAndSave = () => {
        const newErrors = {};

        if (!taskDetails.title || !taskDetails.title.trim()) {
            newErrors.title = 'Task Name is required';
        }

        const type = (taskDetails.taskType || '').toLowerCase();
        if (!type) {
            newErrors.taskType = 'Task Type is required';
        }

        const targetValStr = taskDetails.targetValue !== undefined && taskDetails.targetValue !== null ? String(taskDetails.targetValue).trim() : '';

        if (type === 'workout') {
            if (!targetValStr) {
                newErrors.targetValue = 'Duration is required';
            } else if (Number(targetValStr) <= 0 || isNaN(Number(targetValStr))) {
                newErrors.targetValue = 'Duration must be a positive number';
            }
        } else if (type === 'food') {
            if (!targetValStr) {
                newErrors.targetValue = 'Target Value is required';
            } else if (Number(targetValStr) <= 0 || isNaN(Number(targetValStr))) {
                newErrors.targetValue = 'Target Value must be a positive number';
            }
            
            const caloriesStr = taskDetails.calories !== undefined && taskDetails.calories !== null ? String(taskDetails.calories).trim() : '';
            if (!caloriesStr) {
                newErrors.calories = 'Calories is required';
            } else if (Number(caloriesStr) < 0 || isNaN(Number(caloriesStr))) {
                newErrors.calories = 'Calories must be 0 or greater';
            }
        } else if (type === 'beverage' || type === 'water') {
            if (!targetValStr) {
                newErrors.targetValue = 'Target Value is required';
            } else if (Number(targetValStr) <= 0 || isNaN(Number(targetValStr))) {
                newErrors.targetValue = 'Target Value must be a positive number';
            }
        }

        const dayNumStr = taskDetails.dayNumber !== undefined && taskDetails.dayNumber !== null ? String(taskDetails.dayNumber).trim() : '';
        if (!dayNumStr) {
            newErrors.dayNumber = 'Day Number is required';
        } else if (Number(dayNumStr) <= 0 || isNaN(Number(dayNumStr))) {
            newErrors.dayNumber = 'Day Number must be a positive number';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
        } else {
            setErrors({});
            handleSaveTask();
        }
    };

    return (
        <Drawer 
            anchor="right"
            open={open} 
            onClose={onClose} 
            PaperProps={{ 
                sx: { 
                    backgroundColor: '#141414', 
                    color: '#fff', 
                    width: { xs: '100%', sm: '400px' }, 
                    p: 0,
                    top: '64px',
                    height: 'calc(100% - 64px)',
                    display: 'flex',
                    flexDirection: 'column'
                } 
            }}
        >
            <div className="flex items-center p-4 pb-2 border-b border-gray-800">
                <IconButton onClick={onClose} sx={{ color: '#aaa', border: '1px solid #333', borderRadius: '8px', p: 1, mr: 2, '&:hover': { color: '#fff', backgroundColor: '#333' } }}>
                    <ArrowBackIosNewIcon sx={{ fontSize: '16px' }} />
                </IconButton>
                <Typography variant="subtitle1" fontWeight="bold">
                    {isEditMode ? 'Edit Task' : 'Create Task'}
                </Typography>
            </div>
            <Box sx={{ flex: 1, p: 4, pt: 3, pb: 2, overflowX: 'hidden', overflowY: 'auto' }}>
                <DarkInput 
                    label="Task Name"
                    placeholder="e.g. Morning Cardio"
                    value={taskDetails.title || ''}
                    onChange={(e) => {
                        setTaskDetails({ ...taskDetails, title: e.target.value });
                        if (errors.title) setErrors({ ...errors, title: '' });
                    }}
                    error={errors.title}
                />
                
                <DarkInput 
                    label="Task Type"
                    select
                    value={(taskDetails.taskType || '').toLowerCase()}
                    onChange={(e) => {
                        setTaskDetails({ ...taskDetails, taskType: e.target.value });
                        setErrors({ ...errors, taskType: '', targetValue: '', calories: '' });
                    }}
                    error={errors.taskType}
                >
                    <MenuItem value="workout">Workout</MenuItem>
                    <MenuItem value="food">Food</MenuItem>
                    <MenuItem value="beverage">Beverage</MenuItem>
                </DarkInput>

                {(taskDetails.taskType || '').toLowerCase() === 'workout' && (
                    <DarkInput 
                        label="Duration (min)"
                        placeholder="45"
                        type="number"
                        value={taskDetails.targetValue || ''}
                        onChange={(e) => {
                            setTaskDetails({ ...taskDetails, targetValue: e.target.value });
                            if (errors.targetValue) setErrors({ ...errors, targetValue: '' });
                        }}
                        endAdornment="min"
                        error={errors.targetValue}
                    />
                )}

                {(taskDetails.taskType || '').toLowerCase() === 'food' && (
                    <>
                        <DarkInput 
                            label="Target Value (e.g. 100)"
                            placeholder="100"
                            value={taskDetails.targetValue || ''}
                            onChange={(e) => {
                                setTaskDetails({ ...taskDetails, targetValue: e.target.value });
                                if (errors.targetValue) setErrors({ ...errors, targetValue: '' });
                            }}
                            endAdornment="gm"
                            error={errors.targetValue}
                        />
                        <DarkInput 
                            label="Calories (kcal)"
                            placeholder="420"
                            type="number"
                            value={taskDetails.calories || ''}
                            onChange={(e) => {
                                setTaskDetails({ ...taskDetails, calories: e.target.value });
                                if (errors.calories) setErrors({ ...errors, calories: '' });
                            }}
                            endAdornment="kcal"
                            error={errors.calories}
                        />
                    </>
                )}

                {((taskDetails.taskType || '').toLowerCase() === 'beverage' || (taskDetails.taskType || '').toLowerCase() === 'water') && (
                    <DarkInput 
                        label="Target Value (e.g. 1)"
                        placeholder="1"
                        value={taskDetails.targetValue || ''}
                        onChange={(e) => {
                            setTaskDetails({ ...taskDetails, targetValue: e.target.value });
                            if (errors.targetValue) setErrors({ ...errors, targetValue: '' });
                        }}
                        endAdornment="L"
                        error={errors.targetValue}
                    />
                )}

                <DarkInput 
                    label="Day Number (for program)"
                    placeholder="1"
                    type="number"
                    value={taskDetails.dayNumber || ''}
                    onChange={(e) => {
                        setTaskDetails({ ...taskDetails, dayNumber: e.target.value });
                        if (errors.dayNumber) setErrors({ ...errors, dayNumber: '' });
                    }}
                    error={errors.dayNumber}
                />
            </Box>

            <Box sx={{ p: 4, pt: 2, pb: 4, borderTop: '1px solid #222', backgroundColor: '#141414' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Button 
                        variant="contained" 
                        fullWidth 
                        onClick={handleValidateAndSave}
                        sx={{ 
                            backgroundColor: '#3F51B5', 
                            color: '#fff', 
                            textTransform: 'none', 
                            py: 1.2,
                            borderRadius: '6px',
                            fontWeight: 'bold',
                            boxShadow: '0 2px 10px rgba(63, 81, 181, 0.25)',
                            transition: 'background-color 0.2s ease, box-shadow 0.2s ease, transform 0.08s ease',
                            '&:hover': { 
                                backgroundColor: '#4F5BCD',
                                boxShadow: '0 6px 18px rgba(63, 81, 181, 0.35)'
                            },
                            '&:active': {
                                transform: 'translateY(1px)'
                            }
                        }}
                    >
                        Save changes
                    </Button>
                    <Button 
                        variant="outlined" 
                        fullWidth 
                        onClick={handleValidateAndSave}
                        sx={{ 
                            borderColor: '#333', 
                            color: '#fff', 
                            textTransform: 'none', 
                            py: 1.2,
                            borderRadius: '6px',
                            backgroundColor: '#0a0a0a',
                            transition: 'all 0.2s ease',
                            '&:hover': { borderColor: '#555', backgroundColor: '#1a1a1a' }
                        }}
                    >
                        Save & Assign
                    </Button>
                </Box>
            </Box>
        </Drawer>
    );
}
